/**
 * Suggestion list behind the send composer's type-to-add To field.
 *
 * Pure and separate from the component because this is the part with rules
 * worth pinning: what the pool is, what comes out of it, and in what order.
 * The component owns focus, keys, and paint.
 *
 * THE POOL IS SUGGESTIONS, NOT PERMISSION. Everything here ends up as a chip
 * the PM can see and remove before he sends, and the server re-validates every
 * address against its own allow-list regardless. Nothing in this file can widen
 * a send on its own.
 */

/**
 * How many rows the popover shows. Enough that a half-typed surname finds the
 * person, short enough that the list never becomes something to read.
 */
export const MAX_RECIPIENT_SUGGESTIONS = 8;

/**
 * Build the visible suggestion rows.
 *
 *   - Pool = the project's contact book (already recency-ordered by the API)
 *     followed by the configured roster. Contact-book order wins on a tie
 *     because "who did I actually email last" is the better guess than stored
 *     roster order.
 *   - Anything already in the field is removed. A roster address is ALWAYS in
 *     the field (as a solid or ghost chip), so in practice the roster arm
 *     contributes nothing today — it is in the pool so the list stays correct
 *     if chips ever become removable.
 *   - Case-insensitive substring match anywhere in the address, so "gav",
 *     "hoffman", and "@hoff" all find gavin@hoffmanconstruction.com. An empty
 *     query matches everything, which is what makes Arrow-Down-on-empty show
 *     the most recent addresses.
 *   - Capped, and de-duped case-insensitively across both arms.
 *
 * Casing of the RETURNED value is the pool's, not the query's — the contact
 * book stores lowercase, the roster stores what the PM typed in Settings, and
 * either is a real address.
 */
export function filterRecipientSuggestions(args: {
  /** Contact book emails, most-recently-used first. */
  contacts: readonly string[];
  /** Configured primary + roster addresses. */
  roster: readonly string[];
  /** Every address currently rendered as a chip, on or off. */
  chipped: readonly string[];
  /** What the PM has typed so far. */
  query: string;
  limit?: number;
}): string[] {
  const limit = args.limit ?? MAX_RECIPIENT_SUGGESTIONS;
  const needle = args.query.trim().toLowerCase();
  const taken = new Set(args.chipped.map((e) => e.trim().toLowerCase()).filter(Boolean));
  const out: string[] = [];
  for (const raw of [...args.contacts, ...args.roster]) {
    if (typeof raw !== 'string') continue;
    const email = raw.trim();
    if (email === '') continue;
    const key = email.toLowerCase();
    if (taken.has(key)) continue;
    if (needle !== '' && !key.includes(needle)) continue;
    taken.add(key); // doubles as the de-dupe across the two arms
    out.push(email);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Move the highlighted row by `delta`, wrapping at both ends — Gmail's
 * behavior, and the same wrap the design-system Dropdown uses. `-1` (nothing
 * highlighted) enters the list at the top going down and at the bottom going
 * up. An empty list stays at -1.
 */
export function nextActiveIndex(current: number, delta: number, count: number): number {
  if (count <= 0) return -1;
  if (current < 0) return delta > 0 ? 0 : count - 1;
  return (current + delta + count) % count;
}
